import React, { useState } from "react";

type CreateNewAMPProps = {
  onClose: () => void;
  onSubmit: (ampData: {
    name: string;
    description: string;
    instructions: string;
    knowledge: string;
    knowledgeFile: File | null;
  }) => void;
};

export default function CreateNewAMP({ onClose, onSubmit }: CreateNewAMPProps) {
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newInstructions, setNewInstructions] = useState('');
  const [newKnowledge, setNewKnowledge] = useState('');
  const [knowledgeFile, setKnowledgeFile] = useState<File | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: newName,
      description: newDescription,
      instructions: newInstructions,
      knowledge: newKnowledge,
      knowledgeFile
    });
  };

  return (
    <div className="create-amp-container">
      <div className="create-amp-card">
        <div className="create-amp-header">
          <h2>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            Create New AMP
          </h2>
          <p className="create-amp-subtitle">
            Design your custom AI-powered module with advanced capabilities
          </p>
        </div>
        
        <div className="create-amp-body">
          <form onSubmit={handleSubmit} className="create-amp-form">
            {/* Basic Fields Column */}
            <div className="create-amp-column basic-fields">
              <div className="form-field">
                <label>AMP Name</label>
                <input 
                  type="text"
                  value={newName} 
                  onChange={e => setNewName(e.target.value)} 
                  placeholder="e.g., Smart Document Analyzer"
                  required 
                />
              </div>

              <div className="form-field">
                <label>Category</label>
                <select>
                  <option value="">Select a category</option>
                  <option value="document">Document Processing</option>
                  <option value="communication">Communication</option>
                  <option value="analysis">Data Analysis</option>
                  <option value="generation">Content Generation</option>
                  <option value="automation">Process Automation</option>
                </select>
              </div>

              <div className="form-field flex-grow">
                <label>Description</label>
                <textarea 
                  value={newDescription} 
                  onChange={e => setNewDescription(e.target.value)} 
                  placeholder="Describe what this AMP does and how it helps users..."
                />
              </div>

              <div className="form-field flex-grow">
                <label>Instructions</label>
                <textarea 
                  value={newInstructions} 
                  onChange={e => setNewInstructions(e.target.value)} 
                  placeholder="Provide specific instructions for how the AMP should behave, process inputs, and generate outputs..."
                />
              </div>
            </div>

            {/* Knowledge Column */}
            <div className="create-amp-column knowledge-fields">
              <div className="form-field flex-grow">
                <label>Knowledge Base</label>
                <textarea 
                  value={newKnowledge} 
                  onChange={e => setNewKnowledge(e.target.value)} 
                  placeholder="Add domain-specific knowledge, examples, best practices, or context that the AMP should reference when processing requests..."
                  className="knowledge-textarea"
                />
              </div>

              <div className="form-field">
                <label>Upload Knowledge Files</label>
                <div 
                  className={`file-upload-zone ${knowledgeFile ? 'has-file' : ''}`}
                  onClick={() => document.getElementById('knowledge-file-input')?.click()}
                >
                  <div className="file-upload-icon">
                    {knowledgeFile ? '📁' : '📤'}
                  </div>
                  <div className="file-upload-text">
                    {knowledgeFile ? knowledgeFile.name : 'Drop files or click to upload'}
                  </div>
                  <div className="file-upload-subtext">
                    {knowledgeFile ? 'Ready to upload' : 'Supports PDF, TXT, DOCX, MD files'}
                  </div>
                  {knowledgeFile && (
                    <button 
                      type="button" 
                      className="remove-file-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setKnowledgeFile(null);
                      }}
                    >
                      Remove File
                    </button>
                  )}
                </div>
                <input 
                  id="knowledge-file-input"
                  type="file" 
                  onChange={e => setKnowledgeFile(e.target.files?.[0] ?? null)}
                  accept=".txt,.pdf,.docx,.md"
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </form>
        </div>

        <div className="create-amp-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-create" onClick={handleSubmit}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            Create AMP
          </button>
        </div>
      </div>
    </div>
  );
}